import { useCallback } from 'react';
import { useApi } from './useApi';
import type { ContactsApiResponse, StubResponse } from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import { mapContactApiToUi } from '@/lib/mappers/customer-detail';

interface ContactInput {
  name: string;
  email: string;
  phone: string;
  company?: string;
  address?: string;
  notes?: string;
  isPrimary: boolean;
}

interface ContactUpdateInput {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
  isPrimary?: boolean;
}

/**
 * Hook for customer contact operations
 * Uses authenticated API calls with proper headers
 */
export function useCustomerContacts(customerId: string) {
  const { apiGet, apiPost, apiPut, apiDelete } = useApi();

  const getContacts = useCallback(async () => {
    try {
      const response = await apiGet<ContactsApiResponse | StubResponse>(
        `/api/v1/customers/${customerId}/contacts`
      );
      
      if (isStubResponse(response)) {
        console.log("Contacts endpoint not yet implemented");
        return [];
      }
      
      return response.contacts.map(mapContactApiToUi);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      return [];
    }
  }, [customerId, apiGet]);

  const createContact = useCallback(async (contact: ContactInput) => {
    return apiPost<unknown>(
      `/api/v1/customers/${customerId}/contacts`,
      contact
    );
  }, [customerId, apiPost]);

  const updateContact = useCallback(async (contactId: string, contact: ContactUpdateInput) => {
    return apiPut<unknown>(
      `/api/v1/customers/${customerId}/contacts/${contactId}`,
      contact
    );
  }, [customerId, apiPut]);

  const deleteContact = useCallback(async (contactId: string) => {
    return apiDelete<void>(
      `/api/v1/customers/${customerId}/contacts/${contactId}`
    );
  }, [customerId, apiDelete]);

  const setContactAsPrimary = useCallback(async (contactId: string) => {
    return updateContact(contactId, { isPrimary: true });
  }, [updateContact]);

  return {
    getContacts,
    createContact,
    updateContact,
    deleteContact,
    setContactAsPrimary,
  };
}
